import type { DragEndEvent } from '@dnd-kit/core';
import { parseDragId, parseReorderId } from './types';
import { materializeGroupsFromStore } from '../../stores/CanvasContext';
import { globalTasksStore } from '../../../stores/globalTasksStore';
import type { CanvasStore } from '../../stores/canvasStore';

export function handleLayerPanelDragEnd(
  event: DragEndEvent,
  store: CanvasStore,
): void {
  const { active, over } = event;
  if (!over) {
    console.log('[LayerPanel DnD] no over', { over });
    return;
  }

  // Read from store at event time
  const ctx = store.getSnapshot().context;
  const tasksById = globalTasksStore.getSnapshot().context.tasksById;
  const matGroups = materializeGroupsFromStore(store, tasksById);
  const matEntities = Object.values(ctx.nodes);

  // Compute top-level entity order inline
  const groupIdSet = new Set(matGroups.map(g => g.id));
  const validEntityIds = new Set(matEntities.map(e => e.id));
  const validTopLevelIds = new Set([...validEntityIds, ...groupIdSet]);
  const allMemberIds = new Set<string>();
  for (const g of matGroups) { for (const mid of g.memberEntityIds) allMemberIds.add(mid); }
  const allNonConnectors = [...matEntities, ...matGroups.map(g => {
    const groupEntities = Object.values(ctx.groups);
    return groupEntities.find(e => e.id === g.id);
  }).filter(Boolean)] as import('../../entityTypes').CanvasEntity[];
  const matOrder = allNonConnectors
    .filter(e => validTopLevelIds.has(e.id) && !allMemberIds.has(e.id))
    .map(e => e.id);

  const dragged = parseDragId(active.id as string);
  const overId = over.id as string;
  console.log('[LayerPanel DnD] dragEnd', { draggedId: active.id, overId, dragged });

  // Helper: move entity to a target layer
  const moveToLayer = (targetLayerId: string) => {
    if (dragged.kind === 'group') {
      store.send({ type: 'group.setLayer', groupId: dragged.id, layerId: targetLayerId });
    } else {
      store.send({ type: 'entity.setLayer', entityId: dragged.id, layerId: targetLayerId });
    }
  };

  // Drop on a reorder target (another item)
  if (overId.startsWith('reorder:')) {
    const target = parseReorderId(overId);
    if (target && dragged.id !== target.id) {
      // Try reorder within same group (using full memberEntityIds to preserve interleaving)
      const parentGroup = matGroups.find(g => g.memberEntityIds.includes(dragged.id) && g.memberEntityIds.includes(target.id));
      if (parentGroup) {
        const newOrder = [...parentGroup.memberEntityIds];
        const fromIdx = newOrder.indexOf(dragged.id);
        const toIdx = newOrder.indexOf(target.id);
        if (fromIdx !== -1 && toIdx !== -1 && fromIdx !== toIdx) {
          newOrder.splice(fromIdx, 1);
          newOrder.splice(toIdx, 0, dragged.id);
          console.log('[LayerPanel DnD] group reorder', { groupId: parentGroup.id, newOrder });
          store.send({ type: 'group.reorderMembers', groupId: parentGroup.id, memberEntityIds: newOrder });
        }
        return;
      }

      // Combined lookup: entities + groups (groups are not in materialized entities)
      const findLayerId = (id: string): string | undefined => {
        const ent = matEntities.find(e => e.id === id);
        if (ent) return ent.layerId;
        const grp = matGroups.find(g => g.id === id);
        return grp?.layerId;
      };

      const targetLayerId = findLayerId(target.id);
      const draggedLayerId = findLayerId(dragged.id);
      if (targetLayerId && draggedLayerId) {
        if (targetLayerId !== draggedLayerId) {
          // Different layers → move to target's layer
          console.log('[LayerPanel DnD] cross-layer move', { from: draggedLayerId, to: targetLayerId });
          moveToLayer(targetLayerId);
        } else {
          // Same layer — check container difference (group vs ungrouped, different groups)
          const draggedGroupId = matGroups.find(g => g.memberEntityIds.includes(dragged.id))?.id ?? null;
          const targetGroupId = matGroups.find(g => g.memberEntityIds.includes(target.id))?.id ?? null;

          if (draggedGroupId === targetGroupId) {
            // Both top-level on same layer → reorder via topLevelEntityOrder
            const layerId = draggedLayerId;
            const topLevelOnLayer = matOrder.filter(id => findLayerId(id) === layerId);
            const fromIdx = topLevelOnLayer.indexOf(dragged.id);
            const toIdx = topLevelOnLayer.indexOf(target.id);
            if (fromIdx !== -1 && toIdx !== -1 && fromIdx !== toIdx) {
              topLevelOnLayer.splice(fromIdx, 1);
              topLevelOnLayer.splice(toIdx, 0, dragged.id);
              console.log('[LayerPanel DnD] top-level reorder', { layerId, topLevelOnLayer });
              store.send({ type: 'entity.reorderInLayer', layerId, orderedIds: topLevelOnLayer });
            }
          } else if (targetGroupId) {
            // Target is in a group → assign dragged to target's group
            console.log('[LayerPanel DnD] assign to group', { targetGroupId, entityId: dragged.id });
            store.send({ type: 'group.assignEntities', groupId: targetGroupId, entityIds: [dragged.id] });
          } else {
            // Target is ungrouped → remove dragged from its current group
            const sourceGroup = matGroups.find(g => g.id === draggedGroupId);
            if (sourceGroup) {
              const newMembers = sourceGroup.memberEntityIds.filter(id => id !== dragged.id);
              console.log('[LayerPanel DnD] remove from group', { groupId: draggedGroupId, entityId: dragged.id });
              store.send({ type: 'group.setMembers', groupId: draggedGroupId!, memberEntityIds: newMembers });
            }
          }
        }
      }
    }
    return;
  }

  // Drop on layer zone
  if (overId.startsWith('layer:')) {
    const targetLayerId = overId.slice('layer:'.length);
    console.log('[LayerPanel DnD] layer zone drop', { targetLayerId });
    moveToLayer(targetLayerId);
    return;
  }

  // Drop on group zone
  if (overId.startsWith('dropgroup:')) {
    const targetGroupId = overId.slice('dropgroup:'.length);
    if (dragged.kind !== 'group') {
      // Cross-layer guard: reject if entity and group are on different layers
      const groupEntities = Object.values(ctx.groups);
      const groupEntity = groupEntities.find(e => e.id === targetGroupId);
      const draggedEntity = matEntities.find(e => e.id === dragged.id) ?? groupEntities.find(e => e.id === dragged.id);
      if (groupEntity && draggedEntity && groupEntity.layerId !== draggedEntity.layerId) return;
      console.log('[LayerPanel DnD] group zone drop', { targetGroupId });
      store.send({ type: 'group.assignEntities', groupId: targetGroupId, entityIds: [dragged.id] });
    }
  }
}
